import { ArrowLeft, Flag, Target, TrendingDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import type { FriendProfile as Profil } from '../../lib/friends.ts';
import { getFriendProfile } from '../../lib/friends.ts';
import { formatHandicap } from '../../lib/me.ts';
import { Carte } from '../rounds/Carte.tsx';

/* Le profil d'un partenaire.
 *
 * ── Pourquoi ses trois derniers tours, et pas son année ────────────────────
 *
 * On ouvre cet écran pour deux raisons : savoir avec qui l'on joue avant de
 * l'inviter, et féliciter quelqu'un qui vient de bien jouer. Les deux se
 * satisfont de trois tours. Une liste sans fin donnerait l'impression de
 * fouiller, ce qui n'est ni utile ni très élégant entre partenaires.
 *
 * ── Pourquoi les boutons d'invitation sont ici ─────────────────────────────
 *
 * C'est le moment où l'envie vient : on regarde ce qu'a fait quelqu'un, et on
 * a envie de jouer avec lui. L'obliger à repasser par la liste des parcours
 * pour l'y retrouver perdrait l'élan.
 */

const jour = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

export function FriendProfile() {
    const { id } = useParams<{ id: string }>();
    const [profil, setProfil] = useState<Profil | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        void getFriendProfile(id)
            .then(setProfil)
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }
    if (!profil) {
        return <div role="alert" className="alert alert-error m-4 text-sm"><span>{error ?? 'Partenaire introuvable'}</span></div>;
    }

    const { friend, rounds, handicaps } = profil;
    // Du plus ancien au plus récent : l'index qui baisse est une progression,
    // et c'est le sens de lecture qui la rend visible.
    const progression = handicaps.length >= 2
        ? handicaps[handicaps.length - 1]!.indexValue - handicaps[0]!.indexValue
        : null;

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/amis" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Mes partenaires
                </Link>
                <div className="flex items-center gap-3">
                    <Avatar name={friend.displayName} email={friend.email} avatarUrl={friend.avatarUrl}
                        size={56} handicap={friend.handicapIndex} />
                    <div className="min-w-0">
                        <h1 className="truncate text-2xl font-bold tracking-tight">{friend.displayName}</h1>
                        <p className="text-sm opacity-70">
                            Partenaire depuis le {jour(friend.friendsSince)}
                        </p>
                    </div>
                </div>
            </header>

            {/* Ce qu'on a fait ENSEMBLE d'abord : c'est ce qui fait le
                partenaire, avant ce qu'il vaut. */}
            <div className="grid grid-cols-3 gap-2 rounded-box bg-base-200 px-2 py-3 text-center">
                <span>
                    <span className="block text-lg font-bold tabular-nums">
                        {formatHandicap(friend.handicapIndex)}
                    </span>
                    <span className="block text-[10px] uppercase tracking-wide opacity-70">index</span>
                </span>
                <span>
                    <span className="block text-lg font-bold tabular-nums">{friend.roundsTogether}</span>
                    <span className="block text-[10px] uppercase tracking-wide opacity-70">parcours ensemble</span>
                </span>
                <span>
                    <span className="block text-lg font-bold tabular-nums">{friend.gamesTogether}</span>
                    <span className="block text-[10px] uppercase tracking-wide opacity-70">putting ensemble</span>
                </span>
            </div>

            {progression != null && progression < 0 && (
                <p className="flex items-center justify-center gap-2 rounded-box bg-base-200 p-3 text-sm">
                    <TrendingDown size={16} className="shrink-0 text-success" aria-hidden="true" />
                    <span>
                        <span className="font-semibold tabular-nums text-success">
                            −{Math.abs(progression).toFixed(1).replace('.', ',')}
                        </span>
                        <span className="opacity-70"> d'index sur ses dernières saisies.</span>
                    </span>
                </p>
            )}

            {/* L'invitation ici même : c'est le moment où l'envie vient. */}
            <div className="grid grid-cols-2 gap-3">
                <Link to={`/parcours?inviter=${friend.id}`}
                    className="btn btn-accent h-20 flex-col gap-1 px-2">
                    <Flag size={22} />
                    <span className="text-sm font-bold leading-tight">Un parcours</span>
                </Link>
                <Link to={`/putting/nouvelle?inviter=${friend.id}`}
                    className="btn btn-secondary h-20 flex-col gap-1 px-2">
                    <Target size={22} />
                    <span className="text-sm font-bold leading-tight">Un putting</span>
                </Link>
            </div>

            <section>
                <h2 className="mb-2 text-sm font-semibold">Ses derniers parcours</h2>
                {rounds.length === 0 ? (
                    <p className="rounded-box bg-base-200 p-4 text-sm opacity-70">
                        Aucun parcours terminé pour l'instant.
                    </p>
                ) : (
                    <ul className="space-y-3">
                        {rounds.map((r) => {
                            const par = r.holes
                                .filter((h) => h.strokes != null)
                                .reduce((s, h) => s + (h.par ?? 0), 0);
                            return (
                                <li key={r.id} className="rounded-box bg-base-200 p-3">
                                    <div className="flex items-baseline justify-between gap-2 px-1">
                                        <span className="min-w-0">
                                            <span className="block truncate font-semibold">{r.courseName}</span>
                                            <span className="block text-xs opacity-60">
                                                {jour(r.startedAt)}{r.teeLabel && ` · ${r.teeLabel}`}
                                            </span>
                                        </span>
                                        <span className="shrink-0 text-right">
                                            <span className="block text-lg font-bold leading-none tabular-nums">
                                                {r.totalStrokes}
                                            </span>
                                            {par > 0 && (
                                                <span className={`block text-xs font-semibold tabular-nums ${
                                                    r.totalStrokes > par ? 'text-error'
                                                        : r.totalStrokes < par ? 'text-success' : 'opacity-60'
                                                }`}>
                                                    {r.totalStrokes === par
                                                        ? 'par'
                                                        : `${r.totalStrokes > par ? '+' : '−'}${Math.abs(r.totalStrokes - par)}`}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="mt-2">
                                        <Carte
                                            holes={r.holes.map((h) => ({
                                                number: h.number, par: h.par,
                                                strokeIndex: null, distanceM: null,
                                            }))}
                                            myHoles={r.holes.map((h) => ({
                                                holeNumber: h.number, strokes: h.strokes, putts: null,
                                            }))}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
}
